import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  Chip,
  Divider,
  Avatar,
  Grid,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import axios from "axios";
import { useMediaQuery, useTheme } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AttachmentIcon from "@mui/icons-material/Attachment";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import DeleteConfirmDialog from "../../DeleteConfirmDialog";
import Pagination from "@mui/material/Pagination";

const glass = {
  background: "rgba(255, 255, 255, 0.25)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.4)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  borderRadius: "18px",
};

export default function AnnouncementPage() {
  const userRole = localStorage.getItem("role");
  const token = localStorage.getItem("token");

  // FIX: Staff should default to Parents → DB = "student"
  const [audience, setAudience] = useState(
    userRole === "staff" ? "Class" : "All",
  );

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [scheduleMode, setScheduleMode] = useState("now");
  const [errors, setErrors] = useState({});
  const { showSnackbar } = useSnackbar();
  const [branch, setBranch] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editGUID, setEditGUID] = useState(null);
  const [branchList, setBranchList] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [scheduleValues, setScheduleValues] = useState({ date: "", time: "" });
  const [expiryDate, setExpiryDate] = useState("");
  const [history, setHistory] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteGuid, setDeleteGuid] = useState(null);
  const [classGuid, setClassGuid] = useState("");
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  // active | expired
  const [page, setPage] = useState(1);
  const rowsPerPage = 4; // change to 10 if you want

  // ================================
  // LOAD DATA
  // ================================
  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        const res = await axios.get(
          `${apiEndpoints.announcements}?action=get`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setHistory(res.data);
      } catch (err) {
        showSnackbar("Failed to fetch announcements", "error");
      }
    };

    const loadBranches = async () => {
      try {
        const res = await axios.get(
          `${apiEndpoints.dropdown}?type=branch&mode=initial`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setBranchList(res.data.data);
      } catch (err) {
        showSnackbar("Failed to load branches", "error");
      }
    };

    loadAnnouncements();
    loadBranches();
  }, []);

  const audienceOptions =
    userRole === "staff" ? ["Class"] : ["All", "Teachers", "Parents", "Class"];

  const isExpired = (date) => {
    const today = new Date();
    const expiry = new Date(date);
    return expiry < today;
  };
  const filteredHistory = history.filter((item) => {
    if (statusFilter === "active") {
      return !isExpired(item.expiry_date);
    }
    return isExpired(item.expiry_date);
  });

  useEffect(() => {
    if (userRole === "staff") {
      axios
        .get(`${apiEndpoints.announcements}?action=assigned-classes`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setAssignedClasses(
            Array.isArray(res.data?.data) ? res.data.data : [],
          );
        })
        .catch(() => {
          setAssignedClasses([]);
          showSnackbar("Failed to load classes", "error");
        });
    } else {
      axios
        .get(`${apiEndpoints.class}?action=getAll`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setAssignedClasses(
            Array.isArray(res.data?.data) ? res.data.data : [],
          );
        })
        .catch(() => {
          setAssignedClasses([]);
          showSnackbar("Failed to load classes", "error");
        });
    }
  }, [userRole, token]);

  // ================================
  // TOGGLE STATUS
  // ================================
  const toggleStatus = async (guid) => {
    try {
      await axios.get(
        `${apiEndpoints.announcements}?action=toggle&guid=${guid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setHistory((prev) =>
        prev.map((a) =>
          a.guid === guid
            ? { ...a, isActive: a.isActive === "1" ? "0" : "1" }
            : a,
        ),
      );
      showSnackbar("Status updated successfully!", "success");
    } catch (err) {
      showSnackbar("Failed to update status", "error");
    }
  };

  // ================================
  // HANDLE EDIT
  // ================================
  const handleEdit = (item) => {
    setTitle(item.title);

    const dbToUi = {
      all: "All",
      staff: "Teachers",
      student: "Parents",
      class: "Class",
    };

    setAudience(dbToUi[item.audience] || "All");

    if (item.audience === "class") {
      setClassGuid(item.class_guid);
    } else {
      setClassGuid("");
    }

    setBranch(item.branch_guid);
    setMessage(item.message);
    setExpiryDate(item.expiry_date?.split(" ")[0]);

    setEditGUID(item.guid);
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ================================
  // HANDLE DELETE
  // ================================
  const handleDeleteClick = (guid) => {
    setDeleteGuid(guid);
    setDeleteOpen(true);
  };

  // Close dialog
  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteGuid(null);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    try {
      await axios.get(
        `${apiEndpoints.announcements}?action=delete&guid=${deleteGuid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setHistory((prev) => prev.filter((a) => a.guid !== deleteGuid));
      showSnackbar("Announcement deleted!", "success");
    } catch (err) {
      showSnackbar("Failed to delete", "error");
    } finally {
      handleDeleteClose();
    }
  };

  // ================================
  // PUBLISH / UPDATE
  // ================================
  const publish = async () => {
    let newErrors = {};

    if (!title.trim()) newErrors.title = "Title is required";
    if (!message.trim()) newErrors.message = "Message cannot be empty";
    if (userRole !== "staff" && !branch) {
      newErrors.branch = "Branch is required";
    }

    if (!expiryDate) newErrors.expiryDate = "Select expiry date";
    if (audience === "Class" && !classGuid) {
      newErrors.classGuid = "Please select a class";
    }

    if (scheduleMode === "later") {
      if (!scheduleValues.date) newErrors.date = "Select schedule date";
      if (!scheduleValues.time) newErrors.time = "Select schedule time";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // ⭐ MAP UI → DB ENUM VALUES ⭐
    const audienceMap = {
      All: "all",
      Teachers: "staff",
      Parents: "student",
      Class: "class",
    };

    const audienceToSend =
      userRole === "staff" ? "class" : audienceMap[audience] || "all";

    try {
      if (editMode) {
        // UPDATE
        await axios.post(
          `${apiEndpoints.announcements}?action=update`,
          {
            guid: editGUID,
            title,
            audience: audienceToSend,
            branch,
            message,
            expiryDate,
            classGuid: audience === "Class" ? classGuid : null,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        showSnackbar("Announcement updated!", "success");
      } else {
        // CREATE
        await axios.post(
          `${apiEndpoints.announcements}?action=create`,
          {
            title,
            audience: audienceToSend,
            message,
            branch,
            expiryDate,
            scheduleMode,
            scheduleDate: scheduleValues.date,
            scheduleTime: scheduleValues.time,
            classGuid: audience === "Class" ? classGuid : null,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        showSnackbar("Announcement created!", "success");
      }

      const res = await axios.get(`${apiEndpoints.announcements}?action=get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setHistory(res.data);

      // RESET FORM
      setTitle("");
      setMessage("");
      setBranch("");
      setAudience(userRole === "staff" ? "Class" : "All");
      setAttachment(null);
      setExpiryDate("");
      setScheduleMode("now");
      setScheduleValues({ date: "", time: "" });
      setEditMode(false);
      setEditGUID(null);
    } catch (err) {
      showSnackbar("Operation failed", "error");
    }
  };

  // ================================
  // EXPIRY BADGE
  // ================================
  const getExpiryBadge = (date) => {
    const today = new Date();
    const expiry = new Date(date);
    const diff = expiry - today;
    const diffDays = Math.ceil(diff / (1000 * 3600 * 24));

    if (diffDays < 0)
      return { label: "Expired", bg: "#ffebee", color: "#d32f2f" };
    if (diffDays === 0)
      return { label: "Expires Today", bg: "#fff3e0", color: "#ef6c00" };
    if (diffDays === 1)
      return { label: "1 day left", bg: "#e1f5fe", color: "#0277bd" };
    return { label: `${diffDays} days left`, bg: "#e8f5e9", color: "#2e7d32" };
  };

const paginatedHistory = filteredHistory.slice(
  (page - 1) * rowsPerPage,
  page * rowsPerPage,
);
useEffect(() => {
  setPage(1);
}, [statusFilter]);
useEffect(() => {
  setPage(1);
}, [history.length]);

  // ================================
  // UI
  // ================================
  return (
    <Box sx={{ p: 3, ...glass }}>
      {/* HEADER */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        mb={4}
        sx={{
          flexWrap: "wrap",
          justifyContent: isMobile ? "center" : "flex-start",
        }}
      >
        <Avatar
          sx={{
            background: "#ffffffaa",
            width: isMobile ? 38 : 50,
            height: isMobile ? 38 : 50,
          }}
        >
          <NotificationsActiveIcon
            sx={{ color: "#3a3d43ff", fontSize: isMobile ? 20 : 28 }}
          />
        </Avatar>

        <Typography
          variant={isMobile ? "h6" : "h4"}
          sx={{
            fontWeight: 700,
            color: "#3a3d43ff",
          }}
        >
          Announcements
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
        {/* LEFT PANEL */}
        <Paper sx={{ flex: 2, p: 4, ...glass }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
            Create Announcement
          </Typography>

          <Stack spacing={3}>
            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
            />
            {userRole !== "staff" && (
              <TextField
                label="Branch"
                select
                fullWidth
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                error={!!errors.branch}
                helperText={errors.branch}
              >
                {branchList.map((b) => (
                  <MenuItem key={b.guid} value={b.guid}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {/* FIXED AUDIENCE DROPDOWN */}
            {userRole !== "staff" && (
              <TextField
                label="Audience"
                select
                fullWidth
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
              >
                {audienceOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {(audience === "Class" || userRole === "staff") && (
              <TextField
                label="Select Class"
                select
                fullWidth
                value={classGuid}
                onChange={(e) => setClassGuid(e.target.value)}
                error={!!errors.classGuid}
                helperText={errors.classGuid}
              >
                {assignedClasses.map((cls) => (
                  <MenuItem key={cls.guid} value={cls.guid}>
                    {cls.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Content Message"
              multiline
              minRows={4}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={!!errors.message}
              helperText={errors.message}
            />

            <TextField
              label="Expiry Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              error={!!errors.expiryDate}
              helperText={errors.expiryDate}
            />

            {/* PUBLISH MODE */}
            <TextField
              label="Publish Mode"
              select
              fullWidth
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value)}
            >
              <MenuItem value="now">Publish Now</MenuItem>
              <MenuItem value="later">Schedule</MenuItem>
            </TextField>

            {scheduleMode === "later" && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Select Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={scheduleValues.date}
                  onChange={(e) =>
                    setScheduleValues((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                  error={!!errors.date}
                  helperText={errors.date}
                />

                <TextField
                  label="Select Time"
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  value={scheduleValues.time}
                  onChange={(e) =>
                    setScheduleValues((prev) => ({
                      ...prev,
                      time: e.target.value,
                    }))
                  }
                  error={!!errors.time}
                  helperText={errors.time}
                />
              </Stack>
            )}

            <Button variant="contained" sx={{ py: 1.2 }} onClick={publish}>
              {editMode
                ? "Update Announcement"
                : scheduleMode === "later"
                  ? "Schedule Announcement"
                  : "Publish Announcement"}
            </Button>
          </Stack>
        </Paper>

        {/* RIGHT PANEL - FEED */}
        <Paper sx={{ flex: 1.5, p: 4, ...glass }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
            Announcement Feed
          </Typography>
          <Stack direction="row" spacing={2} mb={3}>
            <Button
              variant={statusFilter === "active" ? "contained" : "outlined"}
              color="success"
              size="small"
              onClick={() => setStatusFilter("active")}
            >
              Active
            </Button>

            <Button
              variant={statusFilter === "expired" ? "contained" : "outlined"}
              color="error"
              size="small"
              onClick={() => setStatusFilter("expired")}
            >
              Expired
            </Button>
          </Stack>

          <Stack spacing={3}>
            {paginatedHistory.map((item) => {
              const badge = getExpiryBadge(item.expiry_date);

              // MAP DB → UI LABEL
              const dbToUi = {
                all: "All",
                staff: "Teachers",
                student: "Parents",
                class: "Class",
              };

              return (
                <Paper
                  key={item.guid}
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    background: "rgba(255, 255, 255, 0.5)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: "#374259" }}
                    >
                      {isMobile
                        ? item.title.length > 10
                          ? item.title.substring(0, 10) + "..."
                          : item.title
                        : item.title}
                    </Typography>

                    <Switch
                      checked={item.isActive === "1" || item.isActive === 1}
                      disabled={isExpired(item.expiry_date)}
                      onChange={() => toggleStatus(item.guid)}
                      color="success"
                    />
                  </Stack>

                  {item.staff_name && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#546e7a",
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      Created by: {item.staff_name}
                    </Typography>
                  )}

                  <Grid
                    items
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                    gap={2}
                  >
                    <Chip
                      label={dbToUi[item.audience] || item.audience}
                      size="small"
                      sx={{
                        background: "#E0F7FA",
                        color: "#00796B",
                        fontWeight: 600,
                      }}
                    />

                    <Chip
                      label={badge.label}
                      size="small"
                      sx={{
                        background: badge.bg,
                        color: badge.color,
                        fontWeight: 600,
                      }}
                    />
                  </Grid>

                  <Typography
                    sx={{ fontSize: 13, color: "#b71c1c", fontWeight: 600 }}
                  >
                    Expiry Date: {item.expiry_date}
                  </Typography>

                  {isMobile
                    ? item.message.length > 10
                      ? item.message.substring(0, 10) + "..."
                      : item.message
                    : item.message}

                  {item.attachment && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mt={1}
                    >
                      <AttachmentIcon sx={{ fontSize: 18, color: "#6c7a95" }} />
                      <Typography sx={{ fontSize: 13 }}>
                        {item.attachment.name}
                      </Typography>
                    </Stack>
                  )}

                  <Stack direction="row" spacing={2} mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleEdit(item)}
                      sx={{ textTransform: "none", borderRadius: "10px" }}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(item.guid)}
                      sx={{ textTransform: "none", borderRadius: "10px" }}
                    >
                      Delete
                    </Button>
                  </Stack>

                  <Divider sx={{ mt: 2 }} />
                </Paper>
              );
            })}
          </Stack>
          {filteredHistory.length > rowsPerPage && (
            <Stack alignItems="center" mt={3}>
              <Pagination
                count={Math.ceil(filteredHistory.length / rowsPerPage)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          )}
        </Paper>
      </Stack>
      <DeleteConfirmDialog
        open={deleteOpen}
        title="Delete Announcement"
        message="Are you sure you want to delete this announcement?"
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
